import type { AlarmSeverity, AlarmReliability } from "@opus-dt/prisma-schema";

// Graduação de severidade determinística por tipo de causa/evento +
// confiabilidade/instabilidade. Fonte única consumida pelo worker (criação do alarme)
// e pela api/web (derivação no read-model de atenção + sinal de instabilidade). Sem I/O.

/**
 * Causas reconhecidas que geram alarme. Fonte única do tipo (o worker importa
 * daqui em vez de redefinir): adicionar uma causa força, em tempo de compilação, uma
 * entrada em `CAUSE_SEVERITY` e em `DEFAULTS` (expected-action) — nunca um MEDIUM silencioso.
 */
export type CauseType =
  | "STOP_AUTO"
  | "NO_READING"
  | "INCONSISTENT_READING"
  | "MAINTENANCE_NOT_STARTED"
  | "LIMIT_BREACH"; // Spec 078 — leitura numérica fora do limite declarado no parâmetro

// Mapa exaustivo por causa (Record<CauseType> ⇒ cobertura verificada pelo compilador).
const CAUSE_SEVERITY: Record<CauseType, AlarmSeverity> = {
  STOP_AUTO: "HIGH", // parada real de produção
  NO_READING: "MEDIUM", // sem leitura do sensor
  INCONSISTENT_READING: "MEDIUM", // leitura inconsistente
  MAINTENANCE_NOT_STARTED: "MEDIUM", // manutenção não iniciada
  LIMIT_BREACH: "MEDIUM", // fora do limite (ALERT, não ALARM: a máquina segue produzindo)
};

// Eventos sem alarme (NODATA / STOP manual) — severidade derivada na leitura.
const EVENT_SEVERITY: Record<string, AlarmSeverity> = {
  NODATA: "MEDIUM",
  STOP: "MEDIUM",
};

// Fallback quando a causa/evento não é reconhecido: nunca sem severidade.
const DEFAULT_SEVERITY: AlarmSeverity = "MEDIUM";

/** Severidade do alarme a partir do tipo de causa. */
export function resolveAlarmSeverity(causeType: CauseType): AlarmSeverity {
  // O `??` é guarda de runtime para chamadores que driblem o tipo; com Record<CauseType>
  // exaustivo a cobertura é garantida em compilação.
  return CAUSE_SEVERITY[causeType] ?? DEFAULT_SEVERITY;
}

/** Severidade de uma ocorrência sem alarme, derivada do tipo de evento. */
export function severityForEventType(type: string): AlarmSeverity {
  return EVENT_SEVERITY[type] ?? DEFAULT_SEVERITY;
}

/**
 * Confiabilidade derivada da estabilidade da fonte. Nesta entrega o único
 * sinal é o flapping (oscilação) → `SUSPECT`; estável → `HIGH`. Ponto único de mudança
 * quando novos sinais de confiabilidade entrarem.
 */
export function reliabilityFromFlapping(flapping: boolean): AlarmReliability {
  return flapping ? "SUSPECT" : "HIGH";
}

/**
 * Predicado canônico de "fonte instável": alarme oscilando ou confiabilidade
 * suspeita. Fonte única para worker/api/web (a forma do alarme varia — `AlarmSummary`
 * plano ou `AttentionAlarmRef` —, mas os campos `flapping`/`reliability` são os mesmos).
 */
export function isUnstableSignal(a: {
  flapping?: boolean | null;
  reliability?: string | null;
}): boolean {
  return a.flapping === true || a.reliability === "SUSPECT";
}
